/**
 * Actual rent collected per month for one property, from `rent_payments`.
 *
 * Only paid rows count (`status = 'paid'` and `paid_date` present); each is
 * bucketed into its `paid_date` month. The cash-flow view uses this as the
 * strict-actual gross income (no projection) — dated immutable records read
 * live, never frozen into property_log.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";

interface RentPaymentRow {
  paid_date: string | null;
  amount: number | null;
}

export function usePropertyRentPayments(pipelinePropertyId?: string | null) {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  const query = useQuery({
    queryKey: ["property-rent-payments", pipelinePropertyId] as const,
    enabled: Boolean(workspaceId && pipelinePropertyId),
    queryFn: async (): Promise<RentPaymentRow[]> => {
      if (!workspaceId || !pipelinePropertyId) return [];
      const { data, error } = await supabase
        .from("rent_payments")
        .select("paid_date, amount")
        .eq("workspace_id", workspaceId)
        .eq("pipeline_property_id", pipelinePropertyId)
        .eq("status", "paid")
        .not("paid_date", "is", null);
      if (error) throw error;
      return (data ?? []) as RentPaymentRow[];
    },
  });

  // Sum paid rent into "YYYY-MM" buckets.
  const rentByMonth = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of query.data ?? []) {
      if (!r.paid_date) continue;
      const ym = r.paid_date.slice(0, 7);
      map.set(ym, (map.get(ym) ?? 0) + (Number(r.amount) || 0));
    }
    return map;
  }, [query.data]);

  return { rentByMonth, isLoading: query.isLoading };
}
